/**
 * Decodes the payload of a JWT without verifying its signature.
 *
 * This is safe to use for *UI* decisions (e.g. "show the admin link") because
 * the backend independently verifies the token's signature on every
 * protected request — a tampered token will simply be rejected there with a
 * 401/403. This function must never be treated as a security boundary on
 * its own.
 */
export const decodeToken = (token) => {
    if (!token || typeof token !== "string") return null;

    const parts = token.split(".");
    if (parts.length !== 3) return null;

    try {
        const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
        const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
        const json = decodeURIComponent(
            atob(padded)
                .split("")
                .map((c) => "%" + c.charCodeAt(0).toString(16).padStart(2, "0"))
                .join("")
        );
        return JSON.parse(json);
    } catch {
        return null;
    }
};

/**
 * Returns the role claim from the token currently stored in localStorage,
 * or null if there is no valid token. This is the single source of truth
 * for "what role is the current user" on the frontend — it replaces the
 * old pattern of separately storing a `role` string at login time (which
 * could end up out of sync with what the server actually issued).
 */
export const getCurrentRole = () => {
    const token = localStorage.getItem("token");
    const payload = decodeToken(token);
    return payload?.role || null;
};

/**
 * Returns true if the stored token is structurally valid and not expired.
 * Again: this is a UI convenience, not a security check.
 */
export const isTokenPresentAndUnexpired = () => {
    const token = localStorage.getItem("token");
    const payload = decodeToken(token);
    if (!payload) return false;
    if (!payload.exp) return true;
    return payload.exp * 1000 > Date.now();
};
